/**
 * filterPersistence — keeps a list page's `filter` object alive across navigation.
 *
 * Stores in sessionStorage so it survives back/forward and route changes within the same tab,
 * but does not pollute the URL or leak across tabs.
 *
 * Usage in a list page:
 *   import filterPersistence from '@/mixins/filterPersistence'
 *   export default {
 *     mixins: [filterPersistence],
 *     data() {
 *       return {
 *         filterKey: 'utilisateurs-list', // unique per page
 *         filter: { q: '', site_id: null, ... },
 *         pagination: {},
 *       }
 *     },
 *     methods: { fetch(page) { ... } } // existing fetch unchanged
 *   }
 *
 * The mixin restores `filter` (and the last page) BEFORE `created()` runs the page's own
 * `fetch(1)`. To make the page fetch the restored page instead of always page 1, the page
 * can call `this.restoredPage` in its created hook (defaults to 1).
 */

const STORAGE_PREFIX = 'filter:';

export default {
  data() {
    return {
      restoredPage: 1,
    };
  },
  created() {
    this.restoreFilter();
  },
  watch: {
    filter: {
      deep: true,
      handler() {
        this.persistFilter();
      },
    },
  },
  methods: {
    restoreFilter() {
      if (!this.filterKey || !this.filter) return;
      try {
        const raw = sessionStorage.getItem(STORAGE_PREFIX + this.filterKey);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (saved && typeof saved === 'object') {
          if (saved.filter && typeof saved.filter === 'object') {
            // Only copy keys that already exist on this.filter — don't introduce new ones
            Object.keys(this.filter).forEach((key) => {
              if (key in saved.filter) this.filter[key] = saved.filter[key];
            });
          }
          if (saved.page && Number.isFinite(saved.page)) {
            this.restoredPage = saved.page;
          }
        }
      } catch (e) {
        // Corrupted entry — drop it
        sessionStorage.removeItem(STORAGE_PREFIX + this.filterKey);
      }
    },
    persistFilter() {
      if (!this.filterKey || !this.filter) return;
      try {
        const page = (this.pagination && this.pagination.current_page) || 1;
        sessionStorage.setItem(
          STORAGE_PREFIX + this.filterKey,
          JSON.stringify({ filter: this.filter, page }),
        );
      } catch (e) {
        // Quota exceeded or storage disabled — silent fail, no functional impact
      }
    },
    resetFilter() {
      if (!this.filter) return;
      Object.keys(this.filter).forEach((key) => {
        const v = this.filter[key];
        if (typeof v === 'string') this.filter[key] = '';
        else if (Array.isArray(v)) this.filter[key] = [];
        else this.filter[key] = null;
      });
      if (this.filterKey) sessionStorage.removeItem(STORAGE_PREFIX + this.filterKey);
    },
  },
};
